import { supabase } from './supabase';

// ── Types returned by the store-catalog edge function ────────────────────────

export interface StorefrontProduct {
  id: string;
  name: string;
  description: string | null;
  /** Effective sale price — the signed-in customer's custom/tier price when one exists. */
  price: number;
  /** Public catalog price (equals price unless has_custom_price). */
  catalog_price?: number;
  /** True when price is this customer's custom or tier price. */
  has_custom_price?: boolean;
  quantity: number;
  track_inventory: boolean;
  allow_oversell: boolean;
  tax_exempt: boolean;
  /** Extra rates for this product, on top of the business rates. */
  additional_taxes?: Array<{ name: string; rate: number }>;
  /** Refundable per-unit container deposit in cents (0 = none). Never taxed. */
  deposit_cents: number;
  /** Blank → "Deposit". "bottle" → "Bottle deposit". */
  deposit_label?: string | null;
  /** Rented on a recurring agreement instead of sold (rate locks at signup). */
  rental_enabled: boolean;
  rental_price_cents: number;
  rental_interval: 'month' | 'week';
  /** Custom checkout agreement. Blank uses the standard agreement. */
  rental_agreement?: string | null;
  category_id: string | null;
  category: { id: string; name: string } | null;
  product_group_id: string | null;
  option_values: Record<string, string>;
  photo_url: string | null;
}

// Structured address used across checkout, orders, and customer defaults.
export interface StructuredAddress {
  /** Optional business/company name (printed on invoices for B2B orders). */
  company?: string | null;
  line1: string;
  line2?: string | null;
  city: string;
  province: string;
  postal_code: string;
  country: string;
  formatted: string;
}

export function formatStructuredAddress(a: StructuredAddress): string {
  return [a.line1, a.line2, a.city, [a.province, a.postal_code].filter(Boolean).join(' '), a.country]
    .filter(Boolean)
    .join(', ');
}

/** First-3 (FSA) postal prefix — same rule as online-booking service areas. */
export function postalFsaPrefix(postal: string): string {
  return (postal || '').toUpperCase().replace(/\s/g, '').slice(0, 3);
}

/**
 * Per-product delivery surcharge rule (e.g. carry-up fees):
 * for cart quantity q → (q > 0 ? baseFee : 0) + max(0, q - baseUnits) * extraPerUnit.
 * "$2.50/unit" = { baseUnits: 0, baseFee: 0, extraPerUnit: 2.5 };
 * "first 3 = $7.50 flat, then $1.50/unit" = { baseUnits: 3, baseFee: 7.5, extraPerUnit: 1.5 }.
 */
export interface ShippingProductSurcharge {
  stockItemId: string;
  /** Display snapshot; live product name wins when available. */
  productName: string;
  baseUnits: number;
  baseFee: number;
  extraPerUnit: number;
}

export interface ShippingMethod {
  name: string;
  /** Flat price. Legacy entries only have { name, price } (pricing defaults to 'flat'). */
  price: number;
  pricing?: 'flat' | 'by_area';
  /** by_area: rate per service_area. Areas not listed = method unavailable there. */
  areaRates?: Array<{ areaId: string; price: number }>;
  productSurcharges?: ShippingProductSurcharge[];
}

export interface DeliveryAreaInfo {
  id: string;
  name: string;
  prefixes: string[];
}

export interface StorefrontSettings {
  sidebarColor: string;
  sidebarTextColor: string;
  headerColor: string;
  backgroundColor: string;
  accentColor: string;
  businessNameColor: string;
  browseProductsColor: string;
  showBusinessLogo: boolean;
  showBusinessName: boolean;
  showPopularProducts: boolean;
  popularProductIds: string[];
  displayStockLevels: boolean;
  hideOutOfStockProducts: boolean;
  enableInStorePickup: boolean;
  /** Show the delivery instructions / notes box at checkout. */
  enableOrderNotes: boolean;
  pickupAddress: string;
  shippingMethods: ShippingMethod[];
  checkoutMode: 'invoice_on_delivery' | 'pay_at_checkout';
  /** Hosted shop (/store page + portal Shop button). False = embeds only. */
  portalShopEnabled?: boolean;
  embedAllowedOrigins: string[] | null;
  deliveryAreas: { enforced: boolean; prefixes: string[]; areas?: DeliveryAreaInfo[] } | null;
  onlinePaymentsAvailable: boolean;
  /** Credit-card surcharge (display estimate — server computes the real fee). */
  cardSurchargeEnabled?: boolean;
  cardSurchargePercent?: number;
  combinedTaxPercent: number;
  taxRates: Array<{ name: string; ratePercent: number }>;
}

export interface StorefrontBusiness {
  id: string;
  name: string;
  slug: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  logoUrl: string | null;
}

export interface StorefrontData {
  business: StorefrontBusiness;
  settings: StorefrontSettings;
  products: StorefrontProduct[];
  groups: Array<{ id: string; title: string; description: string | null; options: unknown }>;
}

export async function fetchStorefront(
  slug: string,
  portal?: { portalCustomerId: string; portalToken: string } | null
): Promise<StorefrontData> {
  // A valid portal session personalizes prices (customer custom prices / tier);
  // invalid or missing identity silently falls back to public catalog prices.
  const { data, error } = await supabase.functions.invoke('store-catalog', {
    body: { slug, ...(portal || {}) },
  });
  if (error) {
    // supabase-js wraps non-2xx into a generic error; surface the body message.
    const context = (error as { context?: Response }).context;
    if (context) {
      try {
        const body = await context.json();
        throw new Error(body?.error || 'Failed to load store');
      } catch (parseError) {
        if (parseError instanceof SyntaxError) {
          // non-JSON body — generic message below
        } else if (parseError instanceof Error) {
          throw parseError;
        }
      }
    }
    throw new Error('Failed to load store');
  }
  if (data?.error) throw new Error(data.error);
  return data as StorefrontData;
}

// ── Checkout ─────────────────────────────────────────────────────────────────

export interface StoreCheckoutPayload {
  slug: string;
  items: Array<{ stock_item_id: string; quantity: number }>;
  contact: { firstName: string; lastName: string; email: string; phone: string };
  fulfillment: { method: string; address?: string; requestedDate?: string; requestedWindow?: string };
  shippingAddress?: StructuredAddress | null;
  billingAddress?: StructuredAddress | null;
  billingSameAsShipping?: boolean;
  /** Logged-in customers: add the shipping address to their address book. */
  saveShippingAddress?: boolean;
  /** Saved address the customer selected. Deposits are checked against this site. */
  shippingAddressId?: string | null;
  note?: string;
  portalCustomerId?: string;
  portalToken?: string;
}

/** True while the payment link is still awaiting payment. The pay-invoice flow
 *  flips the pending row to completed on success, so "no pending row" = paid
 *  (same anon read the public payment page uses). */
export async function isPaymentStillPending(paymentToken: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('payments')
    .select('id')
    .eq('payment_token', paymentToken)
    .eq('status', 'pending')
    .maybeSingle();
  if (error) return true; // fail closed: keep waiting rather than fake success
  return !!data;
}

export interface StoreCheckoutResult {
  orderId: string;
  orderNumber: string;
  totals: { subtotalCents: number; discountCents: number; taxCents: number; totalCents: number };
  /** rental_prepay: order invoices at delivery, but the rental first period is
   *  charged by card right now (card captured for recurring billing). */
  mode: 'invoice_on_delivery' | 'pay_at_checkout' | 'paid_from_wallet' | 'rental_prepay';
  payToken?: string | null;
  /** Wallet credit auto-applied server-side before any card charge. */
  walletAppliedCents?: number;
  /** rental_prepay: the amount the card payment page collects now. */
  rentalChargeCents?: number;
  /** Recurring rentals created (pending until delivery). */
  rentals?: Array<{ stock_item_id: string; product_name: string; quantity: number; rate_cents: number; interval: string }>;
  /** Inline (pay-first) flow: the card was charged before the order was created. */
  paid?: boolean;
  warning?: string;
  error?: string;
}

/** "$14.99/mo" style label for a rental item. */
export function rentalRateLabel(p: Pick<StorefrontProduct, 'rental_price_cents' | 'rental_interval'>): string {
  return `$${(p.rental_price_cents / 100).toFixed(2)}/${p.rental_interval === 'week' ? 'wk' : 'mo'}`;
}

/** Effective unit price for cart math: rentals use the recurring rate. */
export function effectiveUnitPrice(p: Pick<StorefrontProduct, 'price' | 'rental_enabled' | 'rental_price_cents'>): number {
  return p.rental_enabled ? p.rental_price_cents / 100 : p.price;
}

async function invokeStoreCheckout<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('store-checkout', { body });
  if (error) {
    const context = (error as { context?: Response }).context;
    if (context) {
      try {
        const parsed = await context.json();
        throw new Error(parsed?.error || 'Checkout failed');
      } catch (parseError) {
        if (parseError instanceof SyntaxError) {
          // non-JSON body — generic message below
        } else if (parseError instanceof Error) {
          throw parseError;
        }
      }
    }
    throw new Error('Checkout failed');
  }
  if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
  return data as T;
}

export async function submitStoreCheckout(payload: StoreCheckoutPayload): Promise<StoreCheckoutResult> {
  return invokeStoreCheckout<StoreCheckoutResult>({ ...payload });
}

// ── Inline (pay-first) checkout ──────────────────────────────────────────────
// prepare → confirmCardPayment (client) → finalize. The order only exists
// after finalize verifies the charge — closing the payment screen creates
// nothing in the business's system.

export interface StoreCheckoutPrepareResult {
  /** Set when no card charge applies — fall back to submitStoreCheckout. */
  mode?: 'no_charge';
  checkoutIntentId?: string;
  /** Absent when the wallet covers the whole charge (chargeCents === 0). */
  clientSecret?: string | null;
  chargeCents?: number;
  surchargeCents?: number;
  walletPlanCents?: number;
  quoteTotalCents?: number;
}

export async function prepareStoreCheckout(
  payload: StoreCheckoutPayload & { cardFunding?: string | null }
): Promise<StoreCheckoutPrepareResult> {
  return invokeStoreCheckout<StoreCheckoutPrepareResult>({ ...payload, action: 'prepare' });
}

export async function finalizeStoreCheckout(
  checkoutIntentId: string
): Promise<StoreCheckoutResult | { pending: true }> {
  return invokeStoreCheckout<StoreCheckoutResult | { pending: true }>({ action: 'finalize', checkoutIntentId });
}

export async function storeCheckoutStatus(
  checkoutIntentId: string
): Promise<{ status: string; result: StoreCheckoutResult | null; error: string | null }> {
  return invokeStoreCheckout<{ status: string; result: StoreCheckoutResult | null; error: string | null }>({
    action: 'status',
    checkoutIntentId,
  });
}

// ── Client-side estimate (display only; server recomputes at checkout) ──────

export function estimateCartTax(
  items: Array<{
    product: Pick<StorefrontProduct, 'price' | 'tax_exempt'> &
      Partial<Pick<StorefrontProduct, 'rental_enabled' | 'rental_price_cents' | 'additional_taxes'>>;
    quantity: number;
  }>,
  combinedTaxPercent: number
): number {
  return items.reduce((sum, item) => {
    if (item.product.tax_exempt) return sum;
    const unit = item.product.rental_enabled ? (item.product.rental_price_cents ?? 0) / 100 : item.product.price;
    const extra = (item.product.additional_taxes || []).reduce((n, t) => n + (Number(t.rate) || 0), 0);
    return sum + unit * item.quantity * (combinedTaxPercent + extra) / 100;
  }, 0);
}

export interface CartDepositEstimate {
  /** Refundable deposit charged at checkout, in dollars (never taxed). */
  chargeTotal: number;
  /** Containers assumed to come back as empties (logged-in swap default). */
  swappedTotal: number;
  lines: Array<{ productId: string; name: string; label?: string | null; quantity: number; swapped: number; charge: number }>;
}

/**
 * Mirror of the server's deposit math (display only — the server recomputes at
 * checkout): deposit is charged per container, minus the customer's current
 * container balance for logged-in customers (they swap empties at delivery).
 * `balances` maps stock_item_id → containers on hand; pass nothing for guests.
 */
export function estimateCartDeposits(
  items: Array<{ product: Pick<StorefrontProduct, 'id' | 'name' | 'deposit_cents'> & { deposit_label?: string | null }; quantity: number }>,
  balances?: Record<string, number> | null
): CartDepositEstimate {
  const lines: CartDepositEstimate['lines'] = [];
  for (const { product, quantity } of items) {
    const depositCents = Number(product.deposit_cents) || 0;
    if (depositCents <= 0 || quantity <= 0) continue;
    const swapped = Math.min(Math.max(0, balances?.[product.id] ?? 0), quantity);
    lines.push({
      productId: product.id,
      name: product.name,
      label: product.deposit_label ?? null,
      quantity,
      swapped,
      charge: ((quantity - swapped) * depositCents) / 100,
    });
  }
  return {
    chargeTotal: lines.reduce((n, l) => n + l.charge, 0),
    swappedTotal: lines.reduce((n, l) => n + l.swapped, 0),
    lines,
  };
}

// ── Shipping pricing (mirror of server logic; display only) ─────────────────

export type ShippingBaseResolution =
  | { status: 'priced'; price: number; areaName?: string }
  | { status: 'needs_address' }
  | { status: 'unavailable' };

/**
 * Resolve a method's base price for an address. Flat methods price immediately;
 * by_area methods need a postal code that falls in one of the method's rated
 * areas. The server recomputes this authoritatively at checkout.
 */
export function resolveShippingBase(
  method: ShippingMethod,
  address: { postal_code?: string } | null,
  areas: DeliveryAreaInfo[] | null | undefined
): ShippingBaseResolution {
  if ((method.pricing ?? 'flat') !== 'by_area') {
    return { status: 'priced', price: Number(method.price) || 0 };
  }
  const prefix = postalFsaPrefix(address?.postal_code || '');
  if (prefix.length < 3) return { status: 'needs_address' };
  for (const rate of method.areaRates || []) {
    const area = (areas || []).find(a => a.id === rate.areaId);
    if (area && area.prefixes.includes(prefix)) {
      return { status: 'priced', price: Number(rate.price) || 0, areaName: area.name };
    }
  }
  return { status: 'unavailable' };
}

/** Display label for a method's base price given the current address. */
export function describeShippingPrice(
  method: ShippingMethod,
  address: { postal_code?: string } | null,
  areas: DeliveryAreaInfo[] | null | undefined
): { state: 'free' | 'priced' | 'needs_address' | 'unavailable'; price: number | null; label: string } {
  const res = resolveShippingBase(method, address, areas);
  if (res.status === 'needs_address') return { state: 'needs_address', price: null, label: 'Enter address' };
  if (res.status === 'unavailable') return { state: 'unavailable', price: null, label: 'Not available in your area' };
  if (res.price > 0) return { state: 'priced', price: res.price, label: `$${res.price.toFixed(2)}` };
  return { state: 'free', price: 0, label: 'FREE' };
}

export interface ShippingSurchargeLine {
  stockItemId: string;
  label: string;
  amount: number;
}

/** Per-product delivery surcharges for the current cart (see rule doc above). */
export function computeShippingSurcharges(
  method: ShippingMethod,
  items: Array<{ product: Pick<StorefrontProduct, 'id' | 'name'>; quantity: number }>
): ShippingSurchargeLine[] {
  const lines: ShippingSurchargeLine[] = [];
  for (const rule of method.productSurcharges || []) {
    const entry = items.find(i => i.product.id === rule.stockItemId);
    const q = entry ? Number(entry.quantity) : 0;
    if (q <= 0) continue;
    const amount =
      (Number(rule.baseFee) || 0) +
      Math.max(0, q - (Number(rule.baseUnits) || 0)) * (Number(rule.extraPerUnit) || 0);
    if (amount <= 0) continue;
    lines.push({
      stockItemId: rule.stockItemId,
      label: `Delivery surcharge — ${entry?.product.name || rule.productName}`,
      amount,
    });
  }
  return lines;
}

// ── Shared embed cart (localStorage + BroadcastChannel across the page and tabs) ──

export interface EmbedCartLine {
  productId: string;
  quantity: number;
}

const cartKey = (slug: string) => `nexus-store-cart:${slug}`;
const channelName = (slug: string) => `nexus-store:${slug}`;

export function readEmbedCart(slug: string): EmbedCartLine[] {
  try {
    const raw = localStorage.getItem(cartKey(slug));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(l => l && typeof l.productId === 'string' && l.quantity > 0) : [];
  } catch {
    return [];
  }
}

export function writeEmbedCart(slug: string, lines: EmbedCartLine[]): void {
  try {
    localStorage.setItem(cartKey(slug), JSON.stringify(lines));
    new BroadcastChannel(channelName(slug)).postMessage({ type: 'cart' });
  } catch {
    // storage unavailable (private mode etc.) — cart just won't persist
  }
}

export function subscribeEmbedCart(slug: string, onChange: () => void): () => void {
  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(channelName(slug));
    channel.onmessage = () => onChange();
  } catch {
    // BroadcastChannel unsupported — same-iframe updates still work
  }
  const storageListener = (e: StorageEvent) => {
    if (e.key === cartKey(slug)) onChange();
  };
  window.addEventListener('storage', storageListener);
  return () => {
    channel?.close();
    window.removeEventListener('storage', storageListener);
  };
}
